import React, { useState, useEffect } from 'react';
import strings from '../../resources/strings';
import AddImage from '../AddImage/AddImage';
import { AdditionalExerciseDialogContentProps } from './types';
import { storage } from '../../resources/storage';
import './AdditionalExerciseDialogContent.scss';
import { Checkbox, FormControlLabel, TextField } from '@mui/material';
import SelectControl from '../SelectControl/SelectControl';
import { isArray } from 'lodash';
import { autoId } from '../../utils/UidGenerator';
import { AdditionalExercise } from '../../store/additionalExercises/types';
import { Skill } from '../../store/missions/types';
import { SelectPosition } from '../FieldTextInput/types';
import { Reward } from '../../store/rewards/types';

const MAX_TITLE_LENGTH = 25;

const AdditionalExerciseDialogContent: React.FC<AdditionalExerciseDialogContentProps> = (props) => {
    const [data] = useState(isArray(props.data) ? props.data[0] : props.data);
    const [image, setImage] = useState(data?.image ?? '');
    const [name, setName] = useState<Record<string, string>>(data?.name ?? {});
    const [error, setError] = useState<string>('');

    const [additionalExercise] = useState<AdditionalExercise>(
        data?.['id']
            ? (data as AdditionalExercise)
            : ({
                  id: autoId(),
                  backpack: false,
                  visibleSavedExercises: false,
                  name: {},
                  image: '',
                  tasks: [],
                  skills: [],
                  reward: '',
              } as AdditionalExercise),
    );

    const empty = { value: 'none', label: 'None' };

    const skillsList = [
        empty,
        ...(data?.['missionSkills']?.map((skill: Skill) => {
            return {
                value: skill.id,
                label: skill.name?.[props.selectedLanguage],
            };
        }) as SelectPosition[]),
    ];

    const proficienciesList = [
        empty,
        ...(data?.['proficiencies']?.map((proficiency: Reward) => {
            return {
                value: proficiency.id,
                label: proficiency.name,
            };
        }) as SelectPosition[]),
    ];

    useEffect(() => {
        if (!props.componentData)
            props.setComponentData({
                id: additionalExercise.id,
                name: additionalExercise.name,
                skills: additionalExercise.skills,
                reward: additionalExercise.reward,
                backpack: additionalExercise.backpack,
                image: additionalExercise.image,
                tasks: additionalExercise.tasks,
            });
    }, []);

    useEffect(() => {
        if (props.componentData) props.setComponentData({ ...props.componentData, image: image });
    }, [image]);

    useEffect(() => {
        if (props.componentData) props.setComponentData({ ...props.componentData, name: name });
    }, [name]);

    return (
        <div key={additionalExercise.id}>
            <div className="exercise-dialog-content">
                {props.languages.map((lang) => (
                    <div
                        key={`name_${lang.code}`}
                        style={{ display: lang.code === props.selectedLanguage ? 'block' : 'none' }}
                    >
                        <TextField
                            fullWidth
                            value={name?.[lang.code]}
                            variant="outlined"
                            type="text"
                            id={`name_${lang.code}`}
                            label="Lesson title"
                            name={`name_${lang.code}`}
                            onChange={(event) => {
                                if (event.target.value.length >= MAX_TITLE_LENGTH) {
                                    setError(strings.missions.addMissionDialog.maxTitleLength);
                                    return;
                                }
                                setError('');
                                setName({ ...name, [lang.code]: event.target.value });
                            }}
                            error={error.length > 0}
                            helperText={error}
                        ></TextField>
                    </div>
                ))}
                <AddImage
                    path={storage.exercises}
                    showMessage={false}
                    customName={additionalExercise.id}
                    sendOnDrop={true}
                    setGetImgAddress={(url: string) => setImage(url)}
                    actualEventFile={image}
                ></AddImage>
                <h2>{strings.missions.addExerciseDialog.skillsLabel}</h2>
                <div className="skills-container section">
                    {data?.['missionSkills']?.map((skill: Skill, index: number) => {
                        return (
                            <div className="skill-item" key={skill.id}>
                                <SelectControl
                                    key={index}
                                    selectData={skillsList}
                                    id={skill.id}
                                    index={index}
                                    default={additionalExercise.skills?.includes(skill.id) ? skill.id : 'none'}
                                    label={'Skill'}
                                    fieldName={'skills'}
                                    isArray={true}
                                    singleChoice={true}
                                    componentData={props.componentData}
                                    setComponentData={props.setComponentData}
                                ></SelectControl>
                            </div>
                        );
                    })}
                </div>
                <h2>{strings.additionalMissions.addExerciseDialog.proficienciesLabel}</h2>
                <div className="section wide-select">
                    <SelectControl
                        selectData={proficienciesList}
                        default={data.reward && data.reward !== '' ? data.reward : 'none'}
                        label={'Proficiencies'}
                        fieldName={'reward'}
                        language={props.selectedLanguage}
                        componentData={props.componentData}
                        setComponentData={props.setComponentData}
                    ></SelectControl>
                </div>
                <div className="section">
                    <FormControlLabel
                        control={
                            <Checkbox
                                defaultChecked={additionalExercise.backpack}
                                sx={{ '& .MuiSvgIcon-root': { fontSize: '2.4rem' } }}
                                onChange={(event) => {
                                    props.setComponentData({ ...props.componentData, backpack: event.target.checked });
                                }}
                            />
                        }
                        label={strings.missions.addExerciseDialog.backpack}
                        sx={{ '.MuiFormControlLabel-label': { fontWeight: 'bold', fontSize: '1.2rem' } }}
                    />
                    <FormControlLabel
                        control={
                            <Checkbox
                                defaultChecked={additionalExercise.visibleSavedExercises}
                                sx={{ '& .MuiSvgIcon-root': { fontSize: '2.4rem' } }}
                                onChange={(event) => {
                                    props.setComponentData({
                                        ...props.componentData,
                                        visibleSavedExercises: event.target.checked,
                                    });
                                }}
                            />
                        }
                        label={strings.missions.addExerciseDialog.visibleSavedExercises}
                        sx={{ '.MuiFormControlLabel-label': { fontWeight: 'bold', fontSize: '1.2rem' } }}
                    />
                </div>
            </div>
        </div>
    );
};

export default AdditionalExerciseDialogContent;
